/**
 * DashboardPage — Broker operator dashboard
 *
 * Real-time monitoring and management surface for broker operators.
 * Displays:
 * - System health metrics
 * - Service status overview
 * - Recent activity feed
 * - Quick action controls
 * - Tenant/sub-client statistics
 *
 * DOPLA-597: Broker operator needs a real dashboard
 */

import { useState, useEffect } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import { isAuthenticatedBroker } from '../config/schema';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SystemMetrics {
  activeServices: number;
  activeTenants: number;
  totalRequests24h: number;
  avgResponseTimeMs: number;
  uptime: number;
}

interface ServiceHealth {
  id: string;
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  lastCheck: string;
  responseTime: number;
}

interface ActivityEvent {
  id: string;
  timestamp: string;
  type: 'service.provisioned' | 'tenant.created' | 'binding.created' | 'error';
  message: string;
  actor?: string;
}

interface DashboardData {
  metrics: SystemMetrics;
  services: ServiceHealth[];
  recentActivity: ActivityEvent[];
  timestamp: string;
}

// ── Mock data generator (will be replaced with API call) ─────────────────────

function generateMockDashboardData(): DashboardData {
  return {
    metrics: {
      activeServices: 12,
      activeTenants: 8,
      totalRequests24h: 3847,
      avgResponseTimeMs: 142,
      uptime: 99.97,
    },
    services: [
      {
        id: 'broker-api',
        name: 'Broker API',
        status: 'healthy',
        lastCheck: new Date().toISOString(),
        responseTime: 89,
      },
      {
        id: 'keycloak',
        name: 'Authentication Service',
        status: 'healthy',
        lastCheck: new Date().toISOString(),
        responseTime: 124,
      },
      {
        id: 'postgres',
        name: 'PostgreSQL Database',
        status: 'healthy',
        lastCheck: new Date().toISOString(),
        responseTime: 45,
      },
      {
        id: 'catalog-sync',
        name: 'Catalog Sync Worker',
        status: 'degraded',
        lastCheck: new Date(Date.now() - 120000).toISOString(),
        responseTime: 890,
      },
    ],
    recentActivity: [
      {
        id: 'evt-001',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        type: 'service.provisioned',
        message: 'Service "Deal Manager Pro" provisioned for tenant acme-capital',
        actor: 'admin@doplace.com',
      },
      {
        id: 'evt-002',
        timestamp: new Date(Date.now() - 900000).toISOString(),
        type: 'tenant.created',
        message: 'New tenant "Nordic Fund Partners" created',
        actor: 'operator@doplace.com',
      },
      {
        id: 'evt-003',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        type: 'binding.created',
        message: 'OAuth binding created for tenant alpine-investments',
        actor: 'system',
      },
      {
        id: 'evt-004',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        type: 'error',
        message: 'Catalog sync failed: connection timeout',
        actor: 'system',
      },
      {
        id: 'evt-005',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        type: 'service.provisioned',
        message: 'Service "Analytics Dashboard" provisioned for tenant legacy-trust',
        actor: 'admin@doplace.com',
      },
    ],
    timestamp: new Date().toISOString(),
  };
}

// ── Metric Card component ─────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'stable';
  suffix?: string;
}

function MetricCard({ label, value, trend, suffix }: MetricCardProps) {
  const trendColors = {
    up: 'var(--dp-green-600)',
    down: 'var(--dp-red-600)',
    stable: 'var(--fg-3)',
  };

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-meta)',
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-h2)',
            fontWeight: 600,
            color: 'var(--fg-1)',
          }}
        >
          {value}
        </span>
        {suffix && (
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              color: 'var(--fg-3)',
            }}
          >
            {suffix}
          </span>
        )}
        {trend && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-meta)',
              color: trendColors[trend],
              marginLeft: 'auto',
            }}
          >
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '•'}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Service Health Row component ──────────────────────────────────────────────

interface ServiceHealthRowProps {
  service: ServiceHealth;
}

function ServiceHealthRow({ service }: ServiceHealthRowProps) {
  const statusColors = {
    healthy: 'var(--dp-green-600)',
    degraded: 'var(--dp-orange-600)',
    down: 'var(--dp-red-600)',
  };

  const statusLabels = {
    healthy: 'Healthy',
    degraded: 'Degraded',
    down: 'Down',
  };

  const timeSince = Math.floor((Date.now() - new Date(service.lastCheck).getTime()) / 1000);
  const timeLabel = timeSince < 60 ? `${timeSince}s ago` : `${Math.floor(timeSince / 60)}m ago`;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto auto',
        gap: 'var(--sp-4)',
        alignItems: 'center',
        padding: 'var(--sp-3) var(--sp-4)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            color: 'var(--fg-1)',
            fontWeight: 500,
          }}
        >
          {service.name}
        </p>
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-meta)',
            color: 'var(--fg-3)',
            marginTop: 'var(--sp-1)',
          }}
        >
          {service.id}
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
        }}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: statusColors[service.status],
          }}
        />
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--fg-2)',
          }}
        >
          {statusLabels[service.status]}
        </span>
      </div>

      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--fg-3)',
        }}
      >
        {service.responseTime}ms
      </span>

      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-meta)',
          color: 'var(--fg-3)',
        }}
      >
        {timeLabel}
      </span>
    </div>
  );
}

// ── Activity Event Row component ──────────────────────────────────────────────

interface ActivityEventRowProps {
  event: ActivityEvent;
}

function ActivityEventRow({ event }: ActivityEventRowProps) {
  const typeIcons = {
    'service.provisioned': '⚡',
    'tenant.created': '👥',
    'binding.created': '🔗',
    'error': '⚠️',
  };

  const typeColors = {
    'service.provisioned': 'var(--dp-blue-600)',
    'tenant.created': 'var(--dp-green-600)',
    'binding.created': 'var(--dp-purple-600)',
    'error': 'var(--dp-red-600)',
  };

  const timeSince = Math.floor((Date.now() - new Date(event.timestamp).getTime()) / 60000);
  const timeLabel =
    timeSince < 60 ? `${timeSince}m ago` : `${Math.floor(timeSince / 60)}h ago`;

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: 'var(--radius-md)',
          background: typeColors[event.type] + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '16px' }}>{typeIcons[event.type]}</span>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--fg-1)',
            marginBottom: 'var(--sp-1)',
          }}
        >
          {event.message}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-meta)',
            color: 'var(--fg-3)',
          }}
        >
          <span>{timeLabel}</span>
          {event.actor && <span>• {event.actor}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Main DashboardPage component ──────────────────────────────────────────────

export default function DashboardPage() {
  const config = useConfig();
  const { hasRole } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-refresh dashboard data every 30 seconds
  useEffect(() => {
    function fetchData() {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call to /api/broker/v1/dashboard
      // For now, using mock data generator
      setTimeout(() => {
        try {
          const mockData = generateMockDashboardData();
          setData(mockData);
          setLoading(false);
        } catch (err) {
          setError('Failed to load dashboard data');
          setLoading(false);
        }
      }, 800);
    }

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check if user has operator/admin role (in vertical-ref mode)
  const isAuthedBroker = isAuthenticatedBroker(config.instance.mode);
  const canAccess =
    !isAuthedBroker ||
    hasRole('core-developer') ||
    hasRole('vertical-product-owner') ||
    hasRole('broker-operator');

  if (!canAccess) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-12)',
        }}
      >
        <p className="dp-eyebrow">Access denied</p>
        <h2>Dashboard requires operator access</h2>
        <p
          style={{
            color: 'var(--fg-3)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
          }}
        >
          Your account needs operator or administrative privileges to view the broker dashboard.
        </p>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--sp-12)',
        }}
      >
        <p style={{ color: 'var(--fg-3)', fontFamily: 'var(--font-sans)' }}>
          Loading dashboard...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-12)',
        }}
      >
        <p className="dp-eyebrow">Error</p>
        <h2>Failed to load dashboard</h2>
        <p
          style={{
            color: 'var(--fg-3)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
          }}
        >
          {error}
        </p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div
      className="dp-shell__content"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-6)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p className="dp-eyebrow">Broker Operations</p>
          <h2 style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Dashboard</h2>
          <p
            style={{
              color: 'var(--fg-3)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
            }}
          >
            Real-time monitoring and control surface for {config.instance.name}
          </p>
        </div>
        <button
          className="dp-btn dp-btn--ghost"
          onClick={() => window.location.reload()}
          style={{ alignSelf: 'flex-start' }}
        >
          Refresh
        </button>
      </div>

      {/* Metrics grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--sp-4)',
        }}
      >
        <MetricCard
          label="Active Services"
          value={data.metrics.activeServices}
          trend="stable"
        />
        <MetricCard label="Active Tenants" value={data.metrics.activeTenants} trend="up" />
        <MetricCard
          label="Requests (24h)"
          value={data.metrics.totalRequests24h.toLocaleString()}
          trend="stable"
        />
        <MetricCard
          label="Avg Response"
          value={data.metrics.avgResponseTimeMs}
          suffix="ms"
          trend="stable"
        />
        <MetricCard label="Uptime" value={data.metrics.uptime} suffix="%" trend="stable" />
      </div>

      {/* Service Health section */}
      <div>
        <h3
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-h4)',
            color: 'var(--fg-1)',
            marginBottom: 'var(--sp-3)',
          }}
        >
          Service Health
        </h3>
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          {data.services.map((service) => (
            <ServiceHealthRow key={service.id} service={service} />
          ))}
        </div>
      </div>

      {/* Recent Activity section */}
      <div>
        <h3
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-h4)',
            color: 'var(--fg-1)',
            marginBottom: 'var(--sp-3)',
          }}
        >
          Recent Activity
        </h3>
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          {data.recentActivity.map((event) => (
            <ActivityEventRow key={event.id} event={event} />
          ))}
        </div>
      </div>

      {/* Footer timestamp */}
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-meta)',
          color: 'var(--fg-3)',
          textAlign: 'center',
          paddingTop: 'var(--sp-4)',
        }}
      >
        Last updated: {new Date(data.timestamp).toLocaleString()}
      </div>
    </div>
  );
}
